import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Upload, MessageSquare, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function LookupPage() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [bulk, setBulk] = useState('');
  const [mode, setMode] = useState<'single' | 'bulk'>('single');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);
  const [bulkResults, setBulkResults] = useState<any[]>([]);

  const handleSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setLoading(true);
    try {
      const data = await api.lookup(phone);
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBulk = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBulkResults([]);
    setLoading(true);
    try {
      const data = await api.bulkLookup(bulk);
      setBulkResults(data.results || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const composeFrom = (r: any) => {
    const params = new URLSearchParams();
    if (r.phoneNumber) params.set('phone', r.phoneNumber);
    if (r.gatewayEmail) params.set('to', r.gatewayEmail);
    if (r.carrierName) params.set('carrier', r.carrierName);
    navigate(`/composer?${params.toString()}`);
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Carrier Lookup</h1>
        <p className="text-muted-foreground mt-1">
          Real-time carrier & line-type intelligence via Twilio Lookup v2
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          variant={mode === 'single' ? 'default' : 'outline'}
          onClick={() => setMode('single')}
        >
          Single
        </Button>
        <Button
          variant={mode === 'bulk' ? 'default' : 'outline'}
          onClick={() => setMode('bulk')}
        >
          Bulk
        </Button>
      </div>

      {mode === 'single' ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Lookup a number
            </CardTitle>
            <CardDescription>US numbers preferred; international supported where API allows</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSingle} className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-2">
                <Label htmlFor="phone" className="sr-only">
                  Phone
                </Label>
                <Input
                  id="phone"
                  placeholder="+1 555 123 4567 or (555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup'}
              </Button>
            </form>
            {error && (
              <p className="mt-3 text-sm text-destructive bg-destructive/10 rounded p-2">{error}</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Bulk lookup
            </CardTitle>
            <CardDescription>Paste up to 50 numbers (one per line, or comma-separated)</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleBulk} className="space-y-3">
              <Textarea
                placeholder={'5551234567\n+14155552671\n(212) 555-0123'}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
                rows={6}
                required
              />
              <Button type="submit" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lookup all'}
              </Button>
            </form>
            {error && (
              <p className="mt-3 text-sm text-destructive bg-destructive/10 rounded p-2">{error}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Single result */}
      {result && (
        <Card
          className={cn(
            result.isVerizon && 'border-red-500/60 ring-1 ring-red-500/30 bg-red-500/5'
          )}
        >
          <CardHeader>
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-lg font-mono">
                {result.nationalFormat || result.phoneNumber}
              </CardTitle>
              {result.isVerizon && <Badge variant="verizon">Verizon Wireless</Badge>}
              {result.valid ? (
                <Badge variant="success">Valid</Badge>
              ) : (
                <Badge variant="destructive">Invalid</Badge>
              )}
              {result.lineType && <Badge variant="secondary">{result.lineType}</Badge>}
            </div>
            <CardDescription>
              E.164: {result.phoneNumber} · Country: {result.countryCode || '—'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-muted-foreground">Carrier</p>
                <p className={cn('font-medium', result.isVerizon && 'text-red-500')}>
                  {result.carrierName || 'Unknown'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Line type</p>
                <p className="font-medium">{result.lineType || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">MCC / MNC</p>
                <p className="font-medium font-mono text-sm">
                  {result.mobileCountryCode || '—'} / {result.mobileNetworkCode || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Gateway</p>
                <p className="font-medium font-mono text-sm">
                  {result.gatewayEmail || 'No mapping'}
                  {result.gatewayStatus && (
                    <Badge
                      variant={
                        result.gatewayStatus === 'active'
                          ? 'success'
                          : result.gatewayStatus === 'degraded'
                            ? 'warning'
                            : 'destructive'
                      }
                      className="ml-2"
                    >
                      {result.gatewayStatus}
                    </Badge>
                  )}
                </p>
              </div>
            </div>
            {result.gatewayEmail && (
              <Button onClick={() => composeFrom(result)} className="w-full sm:w-auto">
                <MessageSquare className="h-4 w-4 mr-2" />
                Compose SMS via Email
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk results */}
      {bulkResults.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Results ({bulkResults.length})</h2>
          {bulkResults.map((r, i) => (
            <Card
              key={i}
              className={cn(
                'p-4',
                r.isVerizon && 'border-red-500/60 bg-red-500/5'
              )}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-mono font-medium">
                    {r.nationalFormat || r.phoneNumber || r.input}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {r.error || r.carrierName || '—'} · {r.lineType || ''}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {r.isVerizon && <Badge variant="verizon">Verizon</Badge>}
                  {r.gatewayEmail && (
                    <Button size="sm" variant="outline" onClick={() => composeFrom(r)}>
                      Compose
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
